import type { PlanInput, PlanResult } from "@decopl/deco-engine";
import { plan as planLocal } from "@decopl/deco-engine";
import { useEffect, useMemo, useState } from "react";
import { DiveProfileChart } from "./components/DiveProfileChart.js";
import { DiverPreview } from "./components/DiverPreview.js";
import { GasManager } from "./components/GasManager.js";
import { GFPanel } from "./components/GFPanel.js";
import { Moon, Sun } from "./components/Icons.js";
import { DecoSchedule, ResultsPanel } from "./components/ResultsPanel.js";
import { Settings } from "./components/Settings.js";
import { StatusPill } from "./components/StatusPill.js";
import { TankSettings } from "./components/TankSettings.js";
import { TissueLoadingChart } from "./components/TissueLoadingChart.js";
import { TweaksPanel } from "./components/TweaksPanel.js";
import { WaypointEditor } from "./components/WaypointEditor.js";
import { STR } from "./i18n.js";
import { useStore } from "./store.js";

export function App() {
  const {
    waypoints,
    gases,
    gfLow,
    gfHigh,
    ascentRate,
    descentRate,
    lastStopDepth,
    stopIncrement,
    theme,
    lang,
    setTheme,
    setLang,
  } = useStore();

  const [result, setResult] = useState<PlanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tweaksOpen, setTweaksOpen] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const t = STR[lang];

  const input: PlanInput = useMemo(
    () => ({
      waypoints,
      gases,
      gasSchedule: [{ startSegmentIdx: 0, gasIdx: 0 }],
      gradientFactors: { low: gfLow, high: gfHigh },
      ascentRate,
      descentRate,
      lastStopDepth,
      stopIncrement,
    }),
    [waypoints, gases, gfLow, gfHigh, ascentRate, descentRate, lastStopDepth, stopIncrement],
  );

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      try {
        const r = planLocal(input);
        if (!cancelled) {
          setResult(r);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "calcul impossible");
          setResult(null);
        }
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [input]);

  return (
    <div className="app">
      <div className="header">
        <div className="brand">
          <div className="brand-mark">
            <svg width={28} height={28} viewBox="0 0 32 32" fill="none">
              <circle cx={16} cy={16} r={14} stroke="currentColor" strokeWidth={1.5} opacity={0.3} />
              <circle cx={16} cy={16} r={9} stroke="currentColor" strokeWidth={1.5} opacity={0.6} />
              <circle cx={16} cy={16} r={3} fill="currentColor" />
              <line x1={16} y1={2} x2={16} y2={6} stroke="currentColor" strokeWidth={1.5} />
              <line x1={16} y1={26} x2={16} y2={30} stroke="currentColor" strokeWidth={1.5} />
              <line x1={2} y1={16} x2={6} y2={16} stroke="currentColor" strokeWidth={1.5} />
              <line x1={26} y1={16} x2={30} y2={16} stroke="currentColor" strokeWidth={1.5} />
            </svg>
          </div>
          <div>
            <div className="brand-name">DeepCalc</div>
            <div className="brand-sub">ZH-L16C · {t.brand_sub}</div>
          </div>
        </div>

        <div className="header-status">
          {result && (
            <>
              <StatusPill
                label={t.max_depth}
                value={result.maxDepth.toFixed(0)}
                unit="m"
                tone="accent"
              />
              <StatusPill
                label={t.runtime}
                value={result.totalRuntime.toFixed(0)}
                unit="min"
                tone="accent"
              />
              <StatusPill label="GF" value={`${gfLow}/${gfHigh}`} />
              <StatusPill
                label={t.tts}
                value={result.timeToSurface.toFixed(0)}
                unit="min"
                tone={result.isNoDeco ? "safe" : "warn"}
              />
            </>
          )}
        </div>

        <div className="header-actions">
          <div className="lang-toggle">
            <button className={lang === "fr" ? "on" : ""} onClick={() => setLang("fr")}>
              FR
            </button>
            <button className={lang === "en" ? "on" : ""} onClick={() => setLang("en")}>
              EN
            </button>
          </div>
          <button
            className={`icon-btn${theme === "light" ? " active" : ""}`}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            title={theme === "dark" ? t.light : t.dark}
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <button
            className={`icon-btn${tweaksOpen ? " active" : ""}`}
            onClick={() => setTweaksOpen((v) => !v)}
            title="Tweaks"
            style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600 }}
          >
            ⚙
          </button>
        </div>
      </div>

      <div className="main">
        <div className="col">
          <WaypointEditor />
          <GasManager />
          <GFPanel />
          <TankSettings />
          <Settings />
        </div>

        <div className="col col-center">
          <div
            className="panel panel-hero"
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <DiveProfileChart result={result} />
            <div style={{ borderTop: "1px solid var(--line)", paddingTop: 0 }}>
              <div
                className="panel-head"
                style={{ borderBottom: "none", paddingBottom: 6 }}
              >
                <h3>
                  <span className="live-dot" />
                  {t.preview}
                </h3>
              </div>
              <DiverPreview result={result} />
            </div>
          </div>
        </div>

        <div className="col">
          <ResultsPanel result={result} error={error} />
          <DecoSchedule result={result} />
          <div className="panel">
            <div className="panel-head">
              <h3>{t.tissues}</h3>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 9,
                  color: "var(--ink-3)",
                  letterSpacing: "0.1em",
                }}
              >
                16 × ZH-L16C · GF{gfHigh}
              </span>
            </div>
            <div className="panel-body flush">
              <TissueLoadingChart result={result} />
            </div>
          </div>
          <div className="disclaimer">{t.disclaimer}</div>
        </div>
      </div>

      {tweaksOpen && <TweaksPanel onClose={() => setTweaksOpen(false)} />}
    </div>
  );
}
